import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { siteNav } from "@/mocks/site/content";
import { useAuth } from "@/hooks/useAuth";

type Props = {
  variant?: "transparent" | "solid";
};

export default function SiteNav({ variant = "transparent" }: Props) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isSolid = variant === "solid" || scrolled;
  const { session } = useAuth();
  const portalTo = session ? "/dashboard" : "/login";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isSolid
          ? "bg-white/95 backdrop-blur border-b border-ink-100"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
          <div
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              isSolid ? "bg-ink-900 text-white" : "bg-white text-ink-900"
            }`}
          >
            <i className="ri-truck-fill text-xl"></i>
          </div>
          <div className={`leading-tight ${isSolid ? "text-ink-900" : "text-white"}`}>
            <div className="font-display text-lg font-bold">Haulmark</div>
            <div
              className={`text-[10px] uppercase tracking-[0.18em] ${
                isSolid ? "text-ink-400" : "text-white/70"
              }`}
            >
              Movers
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {siteNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  isSolid
                    ? isActive
                      ? "text-brand-600"
                      : "text-ink-700 hover:text-ink-900 hover:bg-cream-100"
                    : isActive
                      ? "text-white bg-white/15"
                      : "text-white/85 hover:text-white hover:bg-white/10"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="tel:+13475550142"
            className={`hidden lg:inline-flex items-center gap-2 text-sm font-semibold cursor-pointer whitespace-nowrap ${
              isSolid ? "text-ink-700 hover:text-brand-600" : "text-white/90 hover:text-white"
            }`}
          >
            <i className="ri-phone-line"></i>
            (347) 555-0142
          </a>
          <Link
            to={portalTo}
            className={`hidden md:inline-flex items-center gap-2 px-3.5 py-2.5 rounded-md text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors border ${
              isSolid
                ? "border-ink-200 text-ink-800 hover:bg-cream-100 hover:border-ink-300"
                : "border-white/30 text-white hover:bg-white/10 hover:border-white/50"
            }`}
          >
            <i className="ri-shield-user-line"></i>
            {session ? "Portal" : "Sign in"}
          </Link>
          <Link
            to="/site/contact"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap transition-colors"
          >
            Get a Quote
            <i className="ri-arrow-right-line"></i>
          </Link>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
              isSolid ? "text-ink-900 hover:bg-cream-100" : "text-white hover:bg-white/10"
            }`}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden bg-white border-t border-ink-100 overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-96" : "max-h-0"
        }`}
      >
        <nav className="px-4 py-3 flex flex-col gap-1">
          {siteNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `px-3 py-2.5 rounded-md text-sm font-semibold cursor-pointer ${
                  isActive ? "bg-cream-100 text-brand-600" : "text-ink-700 hover:bg-cream-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to={portalTo}
            className="mt-2 px-3 py-2.5 rounded-md text-sm font-semibold border border-ink-200 text-ink-800 hover:bg-cream-100 cursor-pointer text-center inline-flex items-center justify-center gap-2"
          >
            <i className="ri-shield-user-line"></i>
            {session ? "Portal" : "Sign in"}
          </Link>
          <Link
            to="/site/contact"
            className="px-3 py-2.5 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer text-center"
          >
            Get a Quote
          </Link>
        </nav>
      </div>
    </header>
  );
}